import { useQrDataUrl } from '@/hooks/useQrDataUrl';
import { Button } from '@startupquickstart/frontend';
import { Download, Maximize2 } from 'lucide-react';
import { QRCode } from './QRCode';

interface QrCodeBlockProps {
  /** Payload to encode (e.g. an access code). */
  value: string;
  /** Optional caption shown on the full-screen poster. */
  label?: string;
  /** Rendered size of the inline QR. */
  size?: number;
  alt?: string;
}

/**
 * An inline QR plus actions to **download** it as a printable PNG and **open**
 * it full-screen (the `/qr` poster) for scanning on a phone.
 */
export function QrCodeBlock({ value, label, size = 160, alt }: QrCodeBlockProps) {
  // High-res PNG for printing/saving (independent of the small inline render).
  const pngUrl = useQrDataUrl(value, 1024);

  const download = () => {
    if (!pngUrl) return;
    const a = document.createElement('a');
    a.href = pngUrl;
    a.download = `qr-${value}.png`;
    a.click();
  };

  const openPoster = () => {
    const qs = new URLSearchParams({ value, ...(label ? { label } : {}) });
    window.open(`/qr?${qs.toString()}`, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="flex flex-col items-center gap-3">
      <QRCode value={value} size={size} alt={alt ?? `QR for ${value}`} />
      <div className="flex gap-2">
        <Button type="button" variant="outline" size="sm" onClick={download} disabled={!pngUrl}>
          <Download className="h-3.5 w-3.5" />
          Download
        </Button>
        <Button type="button" variant="outline" size="sm" onClick={openPoster}>
          <Maximize2 className="h-3.5 w-3.5" />
          Open
        </Button>
      </div>
    </div>
  );
}
